import printJS from "print-js";
import React, { useMemo, useState } from "react";
import { isMobile as isMobileDevice } from "react-device-detect";
import { AiOutlinePlus } from "react-icons/ai";
import {
  BsChevronDown,
  BsChevronUp,
  BsPrinter,
  BsTrash3,
} from "react-icons/bs";
import { CiLock } from "react-icons/ci";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { Box, Flex } from "rebass";
import styled from "styled-components";
import useOrderType from "../../hooks/useOrderType";
import useResponsive from "../../hooks/useResponsive";
import B2b from "../../icons/B2b";
import Bin from "../../icons/Bin";
import Plus from "../../icons/Plus";
import Print from "../../icons/Print";
import Search from "../../icons/Search";
import SearchPlus from "../../icons/SearchPlus";
import Info from "../../icons/Info";
import { yodaOrdersActions } from "../../logic/actions";
import { yodaOrdersService } from "../../logic/services";
import { ORDER_STATUS } from "../../model/order";
import { getLocalizedString, theme } from "../../utils";
import getOrderStatus from "../../utils/getOrderStatus";
import config from "../../utils/globalConfig";
import { Price } from "../common/Price";
import Tooltip from "../common/Tooltip";
import BasicPopover from "../common/BasicPopover";
import OrderStatusLegend from "../common/OrderStatusLegend";
import { ToastMessage } from "./ToastMessage";

const OrderDetailText = styled.strong`
  display: inline-block;
  margin-bottom: 15px;
`;

const Order = ({
  id,
  quantityReorder,
  quantitySwap,
  orderDateFe,
  orderNumber,
  type,
  typeId,
  status,
  statusDescription,
  season,
  seasonId,
  customData,
  lineDescription,
  agentId,
  customerId,
  totalReorder,
  totalSwap,
  b2B,
  currencyCode,
  recycled,
}) => {
  const lang = localStorage.getItem("B2BLanguage") === "ITA" ? "it" : "en";

  const {
    color: statusColor,
    label: statusLabel,
    status: orderStatus,
  } = getOrderStatus({ status, statusDescription, recycled }, "STATUS");

  const isB2b = b2B;
  const [hoverEffect, setHoverEffect] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState();
  const [showExpanded, setShowExpanded] = useState(false);
  const [statusLegendOpen, setStatusLegendOpen] = useState(false);
  const [statusLegendMobileOpen, setStatusLegendMobileOpen] = useState(false);

  const dispatch = useDispatch();
  const navigate = useHistory();
  const orderType = config.ORDER_TYPE_REORDER;

  const { isMobile, isTablet } = useResponsive();
  const { isReorderEnabledForOrder, isSwapEnabledForOrder } = useOrderType();

  const isIncompatibleOrderType =
    statusDescription === "Draft" &&
    (!isReorderEnabledForOrder({ seasonId, customData }) ||
      (typeId == config.ORDER_TYPE_SWAP &&
        !isSwapEnabledForOrder({ seasonId, customData })));

  const print = async (e) => {
    e.stopPropagation();
    dispatch(yodaOrdersActions.loading(true));
    const report = await yodaOrdersService.generateReport(
      String(id),
      localStorage.getItem("B2BLanguage") === "ITA" ? "IT" : "EN",
    );
    const res = await blobToBase64Async(report);
    dispatch(yodaOrdersActions.loading(false));
    printJS({ printable: res, type: "pdf", base64: true });
  };

  async function blobToBase64Async(blob) {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onerror = (e) => reject(fileReader.error);
      fileReader.onloadend = (e) => {
        const dataUrl = fileReader.result;
        const base64 = dataUrl.substring(dataUrl.indexOf(",") + 1);
        resolve(base64);
      };
      fileReader.readAsDataURL(blob);
    });
  }

  const totalQuantity = useMemo(() => {
    const total = quantityReorder + quantitySwap;
    return total ? total : "0";
  }, [quantityReorder, quantitySwap]);

  const isDisabled =
    isIncompatibleOrderType || orderStatus == ORDER_STATUS.CANCELLED;

  const handlePopoverOpen = () => {
    setStatusLegendOpen(true);
  };

  const handlePopoverClose = () => {
    setStatusLegendOpen(false);
  };

  const handlePopoverToggle = () => {
    setStatusLegendOpen(!statusLegendOpen);
  };

  const legendPopoverStyle = {
    maxWidth: ["75vw", "60vw"],
    padding: "0",
    wordWrap: "break-word",
  };

  return !isMobile && !isTablet ? (
    <Flex
      width={1}
      flexDirection="column"
      mb={20}
      onMouseEnter={() => {
        if (!isDisabled) setHoverEffect(true);
      }}
      onMouseLeave={() => {
        if (!isDisabled) setHoverEffect(false);
      }}
      sx={{
        cursor: "pointer",
        ...(isDisabled && {
          cursor: "unset",
        }),
      }}
    >
      <ToastMessage
        message={deleteMessage}
        needConfirm={true}
        confirmFunction={() => {
          dispatch(yodaOrdersActions.removeOrder(id));
          setDeleteMessage(null);
        }}
        cancelFunction={() => {
          setDeleteMessage(null);
        }}
        style={{ width: 600 }}
      />

      <Flex
        width={1}
        flexDirection={["column", null, "row"]}
        p={[2, null, 0]}
        sx={{
          fontSize: [".9em", , , , "1em"],
        }}
      >
        <Flex
          flexDirection={["row", null, "column"]}
          backgroundColor={statusColor}
          justifyContent={"space-between"}
          p={2}
          mb={[2, null, 0]}
          mr={[0, null, 2]}
        ></Flex>

        {/* ORDER DETAILS */}
        <Flex
          width={1}
          backgroundColor={hoverEffect ? "#f8f8f8" : "#f2f2f2"}
          p={3}
          sx={{
            color: "#232323",
            position: "relative",
          }}
          flexDirection={["column", null, "row"]}
          onClick={(e) => {
            if (!isDisabled) {
              e.stopPropagation();
              localStorage.setItem("selectedOrder", id);
              if (customerId) {
                localStorage.setItem("selectedCustomer", customerId);
              }
              if (agentId) {
                localStorage.setItem("selectedAgent", agentId);
              }
              if (orderStatus == ORDER_STATUS.CONFIRMED) {
                navigate.push("/orderReport/" + id);
              } else {
                navigate.push("/orderDetail/" + id);
              }
            }
          }}
        >
          <Flex flexDirection={"column"} width={1}>
            <Flex sx={{ flex: 1, width: "100%" }}>
              <Tooltip
                content={
                  isB2b ? getLocalizedString("ORDER_CREATED_IN_B2B") : null
                }
              >
                <Box mb={3} ml={"-7px"} mt={0} width="fit-content">
                  {isB2b ? (
                    <B2b
                      style={{
                        height: 24,
                        position: "relative",
                      }}
                    />
                  ) : (
                    <span
                      style={{ color: hoverEffect ? "#f8f8f8" : "#f2f2f2" }}
                    >
                      empty
                    </span>
                  )}
                </Box>
              </Tooltip>
            </Flex>
            <Flex flexDirection={"row"}>
              <Flex
                sx={{ flex: 1 }}
                flexDirection="column"
                justifyContent="start"
              >
                <Box>
                  {getLocalizedString("Ordine N°")}:{" "}
                  <OrderDetailText>{orderNumber}</OrderDetailText>
                </Box>
                <Box>
                  {getLocalizedString("ORDER_DATE")}:{" "}
                  <OrderDetailText>{orderDateFe || ""}</OrderDetailText>
                </Box>
              </Flex>
              <Flex
                sx={{ flex: 1 }}
                flexDirection="column"
                justifyContent="start"
              >
                <Box>
                  {getLocalizedString("Tipo")}:{" "}
                  <OrderDetailText>{type?.[lang] || ""}</OrderDetailText>
                </Box>
                <Box>
                  {getLocalizedString("Linea")}:{" "}
                  <OrderDetailText>
                    {lineDescription?.[lang] || ""}
                  </OrderDetailText>
                </Box>
              </Flex>
              <Flex
                sx={{ flex: 1 }}
                flexDirection="column"
                justifyContent="start"
              >
                <Box>
                  {getLocalizedString("Stagione")}:{" "}
                  <OrderDetailText>{season?.[lang] || ""}</OrderDetailText>
                </Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <span>
                    {getLocalizedString("Stato")}:{" "}
                    <OrderDetailText
                      style={{
                        textTransform: "uppercase",
                        color: statusColor,
                        marginBottom: 0,
                      }}
                    >
                      {statusLabel}
                    </OrderDetailText>
                  </span>
                  <Box
                    onMouseEnter={handlePopoverOpen}
                    onMouseLeave={handlePopoverClose}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <BasicPopover
                      content={<OrderStatusLegend />}
                      isOpen={statusLegendOpen}
                      containerStyle={legendPopoverStyle}
                    >
                      <Box
                        sx={{
                          cursor: "help",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Info
                          style={{
                            width: "16px",
                            height: "16px",
                            color: "#666",
                          }}
                          onClick={handlePopoverToggle}
                        />
                      </Box>
                    </BasicPopover>
                  </Box>
                </Box>
                {customData?.ponumber && (
                  <Box>
                    {getLocalizedString("PO")}
                    {": "}
                    <OrderDetailText>
                      {customData?.ponumber || "-"}
                    </OrderDetailText>
                  </Box>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        {/*END - ORDER DETAILS */}
        {/*TOTALS */}
        <Flex
          width={[1, null, 300]}
          flexDirection="column"
          style={{
            backgroundColor: hoverEffect ? "#404040" : theme.background,
            fontSize: "0.8em",
          }}
          p={3}
          mt={[2, null, 0]}
          ml={[0, null, 2]}
          alignItems="center"
          justifyContent={"center"}
        >
          <Flex
            width={1}
            justifyContent="space-between"
            style={{
              paddingBottom: orderType === typeId ? "20px" : "3px",
            }}
          >
            <Box color={theme.white}>{getLocalizedString("QUANTITA'")}</Box>
            <Box color={theme.white} alignContent="flex-end">
              <strong>{quantityReorder || 0}</strong>
            </Box>
          </Flex>
          <Flex
            width={1}
            justifyContent="space-between"
            style={{
              borderTop: orderType === typeId ? "1px solid #999" : "none",
              paddingTop: orderType === typeId ? "20px" : "2.5px",
              paddingBottom: orderType === typeId ? "0px" : "10px",
            }}
          >
            <Box color={theme.white} sx={{ paddingRight: "4px" }}>
              {getLocalizedString("TOTALE RIORDINO")}
            </Box>
            <Flex color={theme.white} alignContent="flex-end">
              <Price
                price={totalReorder || 0}
                currency={currencyCode}
                style={{
                  fontWeight: 800,
                }}
              />
            </Flex>
          </Flex>
          {orderType != typeId && (
            <Flex
              width={1}
              justifyContent="space-between"
              style={{
                paddingBottom: "3px",
                paddingTop: "10px",
                borderTop: "1px solid #999",
              }}
            >
              <Box color={theme.white}>{getLocalizedString("QUANTITA'")}</Box>
              <Box color={theme.white} alignContent="flex-end">
                <strong>{quantitySwap || 0}</strong>
              </Box>
            </Flex>
          )}
          {orderType != typeId && (
            <Flex
              width={1}
              justifyContent="space-between"
              style={{
                paddingBottom: "0px",
                paddingTop: "2.5px",
              }}
            >
              <Box color={theme.white}>{getLocalizedString("TOTALE RESO")}</Box>
              <Box color={theme.white} alignContent="flex-end">
                <Price
                  price={totalSwap || 0}
                  currency={currencyCode}
                  style={{
                    fontWeight: 800,
                  }}
                />
              </Box>
            </Flex>
          )}
        </Flex>
        {/*END - TOTALS */}
        {/* BUTTONS */}

        {isIncompatibleOrderType ? (
          <Flex
            flexDirection={["row", null, "column"]}
            backgroundColor={statusColor}
            justifyContent={"space-between"}
            alignItems="center"
            p={["10px"]}
            mt={[2, null, 0]}
            ml={[0, null, 2]}
          >
            {/* delete order */}
            <DeleteOrderButton
              {...{
                orderNumber,
                status,
                orderStatus,
                statusDescription,
                setDeleteMessage,
                hoverEffect,
              }}
            />
            {/* open order detail */}
            <Tooltip content={getLocalizedString("ORDERS_ORDER_ACCESS_DENIED")}>
              <Flex
                width="28px"
                height="28px"
                justifyContent={"center"}
                alignItems={"center"}
                sx={{
                  cursor: "help",
                  borderRadius: "12px",
                  "&:hover": {
                    background: "rgba(0,0,0,.2)",
                  },
                }}
              >
                <CiLock size={22} color="#fff" />
              </Flex>
            </Tooltip>
            <Box height={"28px"} />
          </Flex>
        ) : orderStatus != ORDER_STATUS.CANCELLED ? (
          <Flex
            flexDirection={["row", null, "column"]}
            backgroundColor={statusColor}
            justifyContent={"space-between"}
            alignItems="center"
            p={["10px", null, "20px"]}
            mt={[2, null, 0]}
            ml={[0, null, 2]}
          >
            {/* delete order */}
            <DeleteOrderButton
              {...{
                orderNumber,
                status,
                orderStatus,
                statusDescription,
                setDeleteMessage,
                hoverEffect,
              }}
            />
            {/* open order detail */}
            <Link
              onClick={(e) => {
                e.stopPropagation();
                localStorage.setItem("selectedOrder", id);
                if (customerId) {
                  localStorage.setItem("selectedCustomer", customerId);
                }
                if (agentId) {
                  localStorage.setItem("selectedAgent", agentId);
                }
              }}
              to={
                orderStatus == ORDER_STATUS.CONFIRMED
                  ? "/orderReport/" + id
                  : "/orderDetail/" + id
              }
              style={{
                position: "relative",
                width: "33.4%",
                visibility:
                  isB2b ||
                  orderStatus == ORDER_STATUS.CONFIRMED ||
                  orderStatus == ORDER_STATUS.DRAFT
                    ? ""
                    : "hidden",
              }}
            >
              <Tooltip
                content={getLocalizedString(
                  orderStatus == ORDER_STATUS.DRAFT
                    ? "EDIT_ORDER"
                    : "VIEW_ORDER",
                )}
              >
                <Box
                  sx={{
                    position: "relative",
                    left: "calc(50% - 9px)",
                    width: 0,
                    top: ["2px", null, "0px"],
                  }}
                >
                  {orderStatus == ORDER_STATUS.CONFIRMED && (
                    <Box
                      sx={{
                        position: "relative",
                        top: ["2px", null, "8px"],
                        "&:hover > div": {
                          opacity: "1",
                        },
                      }}
                    >
                      {orderStatus == ORDER_STATUS.CONFIRMED && (
                        <SearchPlus
                          style={{
                            marginTop: 5,
                            color: "#fff",
                            width: "32px",
                            position: "relative",
                            left: "-7px",
                            top: "-6px",
                            zIndex: "1",
                            transform: "scale(0.6)",
                            fill: "white",
                          }}
                        />
                      )}
                      {orderStatus != ORDER_STATUS.CONFIRMED && (
                        <Search
                          style={{
                            marginTop: 5,
                            color: "#fff",
                            width: "24px",
                            position: "relative",
                            left: "2px",
                            zIndex: "1",
                          }}
                        />
                      )}

                      <Box
                        sx={{
                          width: "28px",
                          position: "absolute",
                          height: "28px",
                          marginTop: "-35px",
                          marginLeft: "-5px",
                          borderRadius: "25px",
                          background: "rgba(0,0,0,.2)",
                          opacity: "0",
                        }}
                      ></Box>
                    </Box>
                  )}
                  {orderStatus != ORDER_STATUS.CONFIRMED && (
                    <Box
                      sx={{
                        position: "relative",
                        top: ["2px", null, "2px"],
                        "&:hover > div": {
                          opacity: "1",
                        },
                      }}
                    >
                      <Plus
                        style={{
                          color: "#fff",
                          position: "relative",
                          width: 18,
                          height: 18,
                          marginTop: "-5px",
                          zIndex: "1",
                        }}
                      />
                      <Box
                        sx={{
                          width: "28px",
                          position: "absolute",
                          height: "28px",
                          marginTop: "-23px",
                          marginLeft: "-5px",
                          borderRadius: "25px",
                          background: "rgba(0,0,0,.1)",
                          opacity: "0",
                        }}
                      ></Box>
                    </Box>
                  )}
                </Box>
              </Tooltip>
            </Link>
            {/* print report */}
            <Tooltip content={getLocalizedString("PRINT")}>
              <Box
                sx={{
                  position: "relative",
                  left: "calc(50% - 9px)",
                  width: 0,
                  top: ["2px", null, "2px"],
                  visibility:
                    hoverEffect || isMobileDevice
                      ? ""
                      : ["", "hidden", null, "hidden"],
                  "&:hover > div": {
                    opacity: "1",
                  },
                }}
                onClick={print}
              >
                <Print
                  style={{
                    position: "relative",
                    zIndex: "1",
                    color: "#fff",
                    width: 18,
                    height: 18,
                  }}
                />
                <Box
                  sx={{
                    width: "28px",
                    position: "absolute",
                    height: "28px",
                    marginTop: "-23px",
                    marginLeft: "-5px",
                    borderRadius: "25px",
                    background: "rgba(0,0,0,.1)",
                    opacity: "0",
                  }}
                ></Box>
              </Box>
            </Tooltip>
          </Flex>
        ) : null}

        {orderStatus == ORDER_STATUS.CANCELLED && (
          <Flex
            flexDirection={["row", null, "column"]}
            backgroundColor={statusColor}
            justifyContent={"space-between"}
            p={["10px", null, "20px"]}
            mt={[2, null, 0]}
            ml={[0, null, 2]}
          ></Flex>
        )}
        {/* END - BUTTONS */}
      </Flex>
    </Flex>
  ) : (
    <Flex
      flexDirection="column"
      width={["calc(100% - 20px)", "calc(100% - 20px)", "95%"]}
      mb="20px"
      mx="10px"
    >
      <ToastMessage
        message={deleteMessage}
        needConfirm={true}
        confirmFunction={() => {
          dispatch(yodaOrdersActions.removeOrder(id));
          setDeleteMessage(null);
        }}
        cancelFunction={() => {
          setDeleteMessage(null);
        }}
        style={{ width: 600 }}
      />
      {/* MOBILE ORDER DETAIL */}
      <Flex flexDirection="column" bg="#f4f4f4" px="10px" py="10px">
        <Flex
          onClick={() => setShowExpanded(!showExpanded)}
          width="100%"
          alignItems="center"
          justifyContent={isB2b ? "space-between" : "flex-end"}
        >
          {isB2b ? <B2b style={{ width: "22px", height: "22px" }} /> : null}
          {showExpanded ? <BsChevronDown /> : <BsChevronUp />}
        </Flex>
        <Flex>
          <span style={{ marginRight: "5px" }}>
            {getLocalizedString("Ordine N°")}:
          </span>
          <span style={{ fontWeight: "bold" }}>{orderNumber}</span>
        </Flex>
        <Flex>
          <span style={{ marginRight: "5px" }}>
            {getLocalizedString("Tipo")}:
          </span>
          <span style={{ fontWeight: "bold" }}>{type?.[lang] || ""}</span>
        </Flex>
        <Flex sx={{ alignItems: "center", gap: "10px" }}>
          <span style={{ marginRight: "5px" }}>
            {getLocalizedString("Stato")}:
          </span>
          <span style={{ fontWeight: "bold", color: statusColor }}>
            {statusLabel}
          </span>
          <Box
            sx={{
              cursor: "help",
              display: "flex",
              alignItems: "center",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setStatusLegendMobileOpen(true);
            }}
          >
            <Info
              style={{
                width: "16px",
                height: "16px",
                color: "#666",
              }}
            />
          </Box>
        </Flex>
        {customData?.ponumber && (
          <Flex>
            <span style={{ marginRight: "5px" }}>
              {getLocalizedString("PO")}
              {": "}
            </span>
            <span style={{ fontWeight: "bold" }}>
              {customData?.ponumber || "-"}
            </span>
          </Flex>
        )}

        {showExpanded && (
          <>
            <Flex>
              <span style={{ marginRight: "5px" }}>
                {getLocalizedString("ORDER_DATE")}:
              </span>
              <span style={{ fontWeight: "bold" }}>{orderDateFe || ""}</span>
            </Flex>
            <Flex>
              <span style={{ marginRight: "5px" }}>
                {getLocalizedString("Linea")}:
              </span>
              <span style={{ fontWeight: "bold" }}>
                {lineDescription?.[lang] || ""}
              </span>
            </Flex>
            <Flex>
              <span style={{ marginRight: "5px" }}>
                {getLocalizedString("Stagione")}:
              </span>
              <span style={{ fontWeight: "bold" }}>{season?.[lang] || ""}</span>
            </Flex>
          </>
        )}
      </Flex>
      {/* END - MOBILE ORDER DETAIL */}
      {/* MOBILE ORDER TOTALS & BUTTONS */}
      <Flex>
        {/* totals */}
        <Flex
          flexDirection="column"
          bg="#232323"
          width="100%"
          justifyContent="space-between"
          p="10px"
          onClick={() => {
            if (
              !isDisabled &&
              (statusDescription === "Confirmed" ||
                statusDescription === "WaitingForConfirmation" ||
                statusDescription === "Draft")
            ) {
              status == ORDER_STATUS.CONFIRMED
                ? navigate.push("/orderReport/" + id)
                : navigate.push("/orderDetail/" + id);
            }
          }}
        >
          <Flex width="100%" justifyContent="space-between">
            <span style={{ color: "white" }}>
              {getLocalizedString("QUANTITA'")}
            </span>
            <span style={{ color: "white" }}>{totalQuantity}</span>
          </Flex>

          <Flex
            width="100%"
            justifyContent="space-between"
            style={{ borderTop: type?.en !== "Swap" && "1px solid white" }}
            pt={type?.en !== "Swap" && "22px"}
          >
            <span style={{ color: "white" }}>
              {getLocalizedString("TOTALE RIORDINO")}
            </span>
            <span style={{ color: "white" }}>
              <Price price={totalReorder} currency={currencyCode} />
            </span>
          </Flex>
          {type?.en === "Swap" && (
            <Flex
              width="100%"
              justifyContent="space-between"
              style={{ borderTop: "1px solid white" }}
              pt="8px"
            >
              <span style={{ color: "white" }}>
                {getLocalizedString("TOTALE RESO")}
              </span>
              <span style={{ color: "white" }}>
                <Price price={totalSwap} currency={currencyCode} />
              </span>
            </Flex>
          )}
        </Flex>
        {/* buttons */}
        <Flex
          flexDirection="column"
          width="10%"
          bg={statusColor}
          sx={{
            minHeight: "116px",
            height: "auto",
          }}
        >
          {(statusDescription === "Confirmed" ||
            statusDescription === "WaitingForConfirmation") && (
            <Flex height="100%" alignItems="center" justifyContent="center">
              {" "}
              <SearchPlus
                style={{
                  marginTop: 5,
                  color: "#fff",
                  width: "32px",
                  position: "relative",
                  zIndex: "1",
                  transform: "scale(0.6)",
                  fill: "white",
                }}
              />{" "}
            </Flex>
          )}
          {statusDescription === "Draft" && (
            <Flex
              height="100%"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              style={{ gap: "14px" }}
            >
              <BsTrash3
                color="white"
                size="20px"
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    status == ORDER_STATUS.DRAFT &&
                    statusDescription != "Cancelled"
                  ) {
                    setDeleteMessage(
                      getLocalizedString("DELETEORDER_MESSAGE") +
                        orderNumber +
                        "?",
                    );
                  }
                }}
              />
              {isDisabled ? (
                <>
                  <Flex
                    width="28px"
                    height="28px"
                    justifyContent={"center"}
                    alignItems={"center"}
                    sx={{
                      cursor: "help",
                    }}
                  >
                    <CiLock size={22} color="#fff" />
                  </Flex>
                  <Box height={"20px"} />
                </>
              ) : (
                <>
                  <AiOutlinePlus
                    color="white"
                    size="20px"
                    onClick={(e) => {
                      e.stopPropagation();
                      orderStatus == ORDER_STATUS.CONFIRMED
                        ? navigate.push("/orderReport/" + id)
                        : navigate.push("/orderDetail/" + id);
                    }}
                  />
                  <BsPrinter color="white" size="20px" onClick={print} />
                </>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
      {/* END - MOBILE ORDER TOTALS & BUTTONS */}
      {/* Mobile Status Legend Modal */}
      {statusLegendMobileOpen && (
        <OrderStatusLegend
          mobile={true}
          onClose={() => setStatusLegendMobileOpen(false)}
        />
      )}
    </Flex>
  );
};

const DeleteOrderButton = ({
  orderNumber,
  orderStatus,
  status,
  statusDescription,
  setDeleteMessage,
  hoverEffect,
}) => (
  <Link
    onClick={(e) => {
      if (orderStatus == ORDER_STATUS.DRAFT) {
        setDeleteMessage(
          getLocalizedString("DELETEORDER_MESSAGE") + orderNumber + "?",
        );
      }
    }}
    style={{
      position: "relative",
      width: "33.3%",
      top: "calc(20% - 26px )",
    }}
    to="#"
  >
    <Box
      sx={{
        position: "relative",
        left: "calc(50% - 9px)",
        width: 0,
        visibility:
          hoverEffect || isMobileDevice ? "" : ["", "hidden", null, "hidden"],
      }}
    >
      {orderStatus == ORDER_STATUS.DRAFT && (
        <Tooltip content={getLocalizedString("DELETE_ORDER")}>
          <Box
            sx={{
              position: "relative",
              top: ["2px", null, "2px"],
              "&:hover > div": {
                opacity: "1",
              },
            }}
          >
            <Bin
              style={{
                position: "relative",
                color: "#fff",
                width: 18,
                height: 18,
                zIndex: "1",
              }}
            />
            <Box
              sx={{
                width: "28px",
                position: "absolute",
                height: "28px",
                marginTop: "-23px",
                marginLeft: "-5px",
                borderRadius: "25px",
                background: "rgba(0,0,0,.2)",
                opacity: "0",
              }}
            ></Box>
          </Box>
        </Tooltip>
      )}
      {status == ORDER_STATUS.DRAFT && statusDescription == "Cancelled" && (
        <Box style={{ height: "18px" }}></Box>
      )}
    </Box>
  </Link>
);

export default Order;
